import { CUIColors } from '@dotcom/crown-ui';
import { CardContextState } from '../../../context/card-context';
import colorsList from '../../../styles/util.scss';
import { deepCopy, getFontName, CanvasDataTypes, PHOTO_TEXT_ZONE_DEFAULT_FONT_ID } from '../../../utils';
import { helperSettingsConfig } from '../../../utils/configs/helper-settings.configs';
import { getTextSettingsParams } from '../card-editor-types';

const { scalingFactor } = helperSettingsConfig;
const spacingUnit = 20; // this is the old textPadding

const textDefaultSettings = {
  left: spacingUnit * scalingFactor * 2,
  padding: (0.75 * spacingUnit) / scalingFactor,
  editable: true,
  fill: CUIColors.Black,
  opacity: 1,
};

export function isSpecialCard(cardState: CardContextState) {
  const { isChocolate, isWooden, isPostcard } = cardState;
  return isChocolate || isWooden || isPostcard;
}

// Gets the placeholder text from the DefaultTextArea object
export function getDefaultPlaceholderText(settings, defaultText) {
  const placeHolderText = settings.defaultOptions ? settings.defaultOptions.Text : defaultText;
  return placeHolderText;
}

/*
 * Returns a group of text settings, filtering out unneeded settings not needed for Dynamic Text Box
 */
export function getTextSettings({
  cardState,
  defaultFontSize,
  color,
  isUK,
  name,
  defaultOptions,
}: getTextSettingsParams) {
  const defaultColor = isUK ? colorsList[CUIColors.CharcoalGrey] : color || colorsList[CUIColors.Black];
  // Used to only get the values that we needed from the defaultOptions object and avoid unexpected behavior
  const propsToUse = [
    'IsFixed',
    'IsHybrid',
    'IsMultiline',
    'Rotation',
    'TextAlign',
    'CanResizeTextArea',
    'CanEditFontSize',
    'CanEditFontColor',
    'FontAutoSize',
    'Text',
  ];

  // Map PascalCase API keys to camelCase fabric.js keys
  const keyMapping = new Map<string, string>([['TextAlign', 'textAlign']]);

  // Creates a list of text options based on PropsToUse, converting keys to fabric-compatible casing.
  const dynamicTextBoxOptions = Object.fromEntries(
    Object.entries(defaultOptions || {})
      .filter(([key, value]) => propsToUse.includes(key) && value !== undefined)
      .map(([key, value]) => [keyMapping.get(key) || key, value]),
  );

  // Returns a new object, copying default text settings but overriding various properties to be what we would expect.
  return Object.assign(deepCopy(textDefaultSettings), {
    fontSize: isSpecialCard(cardState) ? 36 : defaultFontSize,
    fill: defaultColor,
    fontFamily: getFontName(defaultOptions?.FontFamilyId || PHOTO_TEXT_ZONE_DEFAULT_FONT_ID),
    name,
    data: {
      type: CanvasDataTypes.Placeholder,
    },
    ...dynamicTextBoxOptions,
  });
}

/**
 * Determines whether the card should show bleed or not
 *
 * @param {CardContextState} cardState card state
 */
export function hasNoBleed(cardState: CardContextState) {
  return cardState.isChocolate || cardState.cardType === 'photo' || cardState.isPostcard;
}
